import { HStack, Icon } from "@chakra-ui/react";

import { Restricted } from "@/app/components/Restricted";
import SimpleSelect from "@/common/components/SimpleSelect";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { MemberRoleDisplayOptions } from "@/global-settings/components/MemberRoleBadge";
import { useMemberRoleUpdateMutation } from "@/global-settings/data/useMemberRoleUpdateMutation";
import { useFlag } from "@reflag/react-sdk";
import { Member } from "@reflag/shared/organizationAPI";
import { MemberRole } from "@reflag/shared/permissions";

type MemberRoleProps = {
  member: Member;
};

export const roleOptions = Object.entries(MemberRoleDisplayOptions).map(
  ([value, option]) => ({
    value: value as MemberRole,
    label: (
      <HStack>
        <Icon as={option.icon} />
        <span>{option.label}</span>
      </HStack>
    ),
    description: option.description,
  }),
);

export function MemberRolePicker({ member }: MemberRoleProps) {
  const { userId } = useEnvironment();
  const { isEnabled: enforceUserPermissions } = useFlag("user-permissions");

  const { mutate: updateMemberRole, isPending: isUpdating } =
    useMemberRoleUpdateMutation(member.id);

  const handleRoleChange = (newRole: MemberRole) => {
    if (newRole !== member.role) {
      updateMemberRole({ role: newRole });
    }
  };

  const isCurrentUser = member.id === userId;

  return enforceUserPermissions ? (
    <SimpleSelect
      _menuList={{ maxW: "lg" }}
      isDisabled={isUpdating || isCurrentUser}
      minW={60}
      options={roleOptions}
      size="sm"
      value={member.role}
      w="full"
      onChange={handleRoleChange}
    />
  ) : (
    <Restricted flagKey="user-permissions">
      <SimpleSelect
        minW={60}
        options={[
          {
            value: "none",
            label: "—",
          },
        ]}
        size="sm"
        value="none"
        w="full"
        isDisabled
      />
    </Restricted>
  );
}
